import { EntityMetadataModel } from '../../../api/api';
import { ExpressionModel } from '../Models/ExpressionModel';
import { ObjectPropertyType } from '../Models/ObjectPropertyType';
import { ObjectSchemaType } from '../Models/ObjectSchemaType';
import { IVariableScope } from '../Models/VariableScopes/IVariableScope';
import { SchemaTypeVariableScope } from '../Models/VariableScopes/SchemaTypeVariableScope';

export type VariableDictionaryKeysRule = {
	nameRef: string;
	keysFromRef: string;
};

function resolveRef(data: Record<string, any>, reference: string): unknown {
	let value: any = data;
	for (const segment of reference.split('.')) {
		value = value?.Type === 'Object' ? value.Properties?.[segment] : value?.[segment];
		if (value === undefined || value === null) return undefined;
	}
	return value;
}

function knownKeys(value: unknown, scope: IVariableScope): string[] {
	if (!value || typeof value !== 'object') return [];
	const expression = value as ExpressionModel;
	if (expression.Type === 'Object') return Object.keys(expression.Properties ?? {});
	if (expression.Type === 'Const') {
		return expression.Value && typeof expression.Value === 'object' && !Array.isArray(expression.Value)
			? Object.keys(expression.Value)
			: [];
	}
	if (expression.Type === 'Var' && Array.isArray(expression.Value)) {
		let currentScope = scope;
		let property: ObjectPropertyType | undefined;
		for (const segment of expression.Value) {
			property = currentScope.getVariable(segment);
			if (!property?.referenceEntity) return [];
			currentScope = new SchemaTypeVariableScope(property.referenceEntity as ObjectSchemaType);
		}
		return property?.referenceEntity?.properties.map(item => item.name) ?? [];
	}
	return [];
}

function addKeysAtPath(property: ObjectPropertyType, path: string[], keys: string[]): ObjectPropertyType {
	const entity = property.referenceEntity as (EntityMetadataModel & ObjectSchemaType) | undefined;
	if (!entity) return property;

	if (path.length === 0) {
		if (!entity.additionalProperties) return property;
		const properties = [...entity.properties];
		for (const key of keys) {
			if (!properties.some(item => item.name === key)) {
				properties.push({ ...entity.additionalProperties, name: key });
			}
		}
		return { ...property, referenceEntity: { ...entity, properties } };
	}

	const [segment, ...remaining] = path;
	const properties = entity.properties.map(item =>
		item.name === segment ? addKeysAtPath(item, remaining, keys) : item,
	);
	return { ...property, referenceEntity: { ...entity, properties } };
}

export function projectVariableDictionaryKeys(
	variableType: ObjectPropertyType,
	variableNameRef: string | undefined,
	rules: VariableDictionaryKeysRule[] | undefined,
	data: Record<string, any>,
	inputScope: IVariableScope,
): ObjectPropertyType {
	let projected = variableType;
	for (const rule of rules ?? []) {
		const [root, ...path] = rule.nameRef.split('.');
		if (root !== variableNameRef || path.length === 0) continue;
		const keys = knownKeys(resolveRef(data, rule.keysFromRef), inputScope);
		if (keys.length > 0) projected = addKeysAtPath(projected, path, keys);
	}
	return projected;
}
